import { useMemo } from 'react';
import {
  Box, SimpleGrid, Icon, useColorModeValue, Text, Flex, Badge,
} from '@chakra-ui/react';
import {
  MdPeople, MdPersonAdd, MdAssignment, MdSecurity,
  MdManageAccounts, MdFactCheck, MdSettings, MdChevronRight,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import Card from 'components/card/Card';
import TodayJornada from 'components/layouts/dashboard/TodayJornada';
import { useAppointments } from 'hooks/useAppointments';
import { usePatients } from 'hooks/usePatients';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { EstadoCita } from 'types/models';

function todayRange() {
  const desde = new Date(); desde.setHours(0, 0, 0, 0);
  const hasta  = new Date(); hasta.setHours(23, 59, 59, 999);
  return { desde: desde.toISOString(), hasta: hasta.toISOString() };
}

interface QuickActionProps {
  icon: any;
  label: string;
  description?: string;
  onClick: () => void;
  accent?: string;
}

function QuickAction({ icon, label, description, onClick, accent = 'brand.500' }: QuickActionProps) {
  const bg         = useColorModeValue('gray.50', 'whiteAlpha.50');
  const hoverBg    = useColorModeValue('brand.50', 'whiteAlpha.100');
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const iconBg     = useColorModeValue('white', 'navy.700');

  return (
    <Flex
      align='center' gap='14px' p='14px' borderRadius='14px' bg={bg}
      _hover={{ bg: hoverBg, cursor: 'pointer', transform: 'translateX(3px)' }}
      transition='all 0.15s' onClick={onClick}
    >
      <Flex w='44px' h='44px' borderRadius='12px' bg={iconBg}
        align='center' justify='center' flexShrink={0}
        boxShadow='0 2px 6px rgba(0,0,0,0.08)'>
        <Icon as={icon} w='22px' h='22px' color={accent} />
      </Flex>
      <Box flex={1} minW={0}>
        <Text fontSize='md' fontWeight='700' color={textColor} noOfLines={1}>{label}</Text>
        {description && <Text fontSize='sm' color={mutedColor} noOfLines={1}>{description}</Text>}
      </Box>
      <Icon as={MdChevronRight} w='18px' h='18px' color={mutedColor} flexShrink={0} />
    </Flex>
  );
}

export default function AdminDashboard() {
  const brandColor  = useColorModeValue('brand.500', 'white');
  const boxBg       = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor   = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor  = useColorModeValue('gray.500', 'gray.400');
  const headerBg    = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  const navigate     = useNavigate();
  const { user }     = useUser();
  const { data: me } = useCurrentDbUser();

  const nombre   = user?.firstName ?? 'Admin';
  const range    = useMemo(todayRange, []);
  const { data: patients } = usePatients();
  const totalPacientes = patients?.length ?? null;

  const dateLabel = new Date().toLocaleDateString('es-EC', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const { data: todayData } = useAppointments(
    me?.id ? { desde: range.desde, hasta: range.hasta, professionalId: me.id, limit: 50 } : {}
  );

  const citasHoy        = todayData?.data?.length ?? 0;
  const citasPendientes = todayData?.data?.filter(a => a.estado === EstadoCita.CONFIRMADA).length ?? 0;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>

      {/* ── Greeting ─────────────────────────────────────────────────── */}
      <Flex
        align='center' justify='space-between' mb='24px'
        p='20px' borderRadius='16px' bg={headerBg}
        border='1px solid' borderColor={borderColor}
        boxShadow='0 1px 4px rgba(0,0,0,0.04)'
        flexWrap='wrap' gap='12px'
      >
        <Box>
          <Text color={textColor} fontSize='xl' fontWeight='700' mb='2px'>
            Panel de Administración 👋
          </Text>
          <Text color={mutedColor} fontSize='sm' textTransform='capitalize'>
            {nombre} · {dateLabel}
          </Text>
        </Box>
        {citasPendientes > 0 && (
          <Badge colorScheme='orange' borderRadius='full' px='14px' py='6px' fontSize='sm'>
            {citasPendientes} cita{citasPendientes !== 1 ? 's' : ''} pendiente{citasPendientes !== 1 ? 's' : ''}
          </Badge>
        )}
      </Flex>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='24px'>
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPeople} color={brandColor} />} />
          }
          name='Total Pacientes'
          value={totalPacientes !== null ? String(totalPacientes) : '—'}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPersonAdd} color={brandColor} />} />
          }
          name='Usuarios Registrados'
          value='—'
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdAssignment} color='teal.400' />} />
          }
          name='Sesiones Hoy'
          value={citasHoy > 0 ? String(citasHoy) : '—'}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={<Icon w='28px' h='28px' as={MdSecurity} color='white' />} />
          }
          name='Eventos de Auditoría'
          value='—'
        />
      </SimpleGrid>

      {/* ── Main content: Jornada + Accesos Rápidos ──────────────────── */}
      <SimpleGrid columns={{ base: 1, xl: 2 }} gap='20px' alignItems='start'
        gridTemplateColumns={{ xl: '1fr 300px' }}>

        <TodayJornada title='Mi Jornada de Hoy' />

        <Card p='20px'>
          <Text color={textColor} fontSize='md' fontWeight='700' mb='4px'>
            Accesos Rápidos
          </Text>
          <Text color={mutedColor} fontSize='xs' mb='16px'>Módulos administrativos</Text>
          <Flex direction='column' gap='8px'>
            <QuickAction
              icon={MdManageAccounts}
              label='Gestión de Usuarios'
              description='Roles, permisos y cuentas'
              onClick={() => navigate('/admin/patients')}
              accent='brand.500'
            />
            <QuickAction
              icon={MdPeople}
              label='Gestión de Pacientes'
              description='Historial y expedientes'
              onClick={() => navigate('/admin/patients')}
              accent='blue.400'
            />
            <QuickAction
              icon={MdFactCheck}
              label='Registro de Auditoría'
              description='Eventos del sistema'
              onClick={() => {}}
              accent='orange.400'
            />
            <QuickAction
              icon={MdSettings}
              label='Configuración del Sistema'
              description='Ajustes generales'
              onClick={() => {}}
              accent='gray.500'
            />
          </Flex>
        </Card>
      </SimpleGrid>
    </Box>
  );
}
